import { apiClient } from './client';
import { getAccessToken } from '@/lib/auth/session';
import { LOGOUT_REQUEST_TIMEOUT_MS } from '@/lib/config';
import type { PurchaseIntent } from '@/lib/constants/issue-report';
import type {
  LoginChallenge,
  LoginOutcome,
  SessionUser,
  SignInRequired,
} from '@/lib/types/auth.types';
import { toSessionUser, type RawSessionUser } from '@/lib/utils/session.utils';

/** The raw wire shape of the session-branch of `POST /auth/login` - `user` not yet converted. */
interface RawLoginSessionResult {
  requires2fa: false;
  accessToken: string;
  user: RawSessionUser;
}

export interface LoginPayload {
  email: string;
  password: string;
}

/**
 * The session-issuing response shape, shared by every endpoint that completes
 * an authentication flow (`/auth/verify-2fa`, `/auth/verify-email`,
 * `/invites/:token/verify-otp`, `/auth/session/refresh`).
 *
 * `POST /auth/login` deliberately does NOT return this any more - see
 * `LoginChallenge` (FR-04, `docs/specs/features/identity-access/02-design.md` §11).
 */
export interface SessionResponse {
  accessToken: string;
  user: SessionUser;
}

/**
 * Step 1 of login: verify credentials against the Identity Provider
 * (server-side; the browser never contacts it).
 *
 * Normally returns the second-factor challenge with no session token yet.
 * While MYN-77 is temporarily disabled (client request, 2026-09-03) the
 * backend instead returns the session directly, with `requires2fa: false` -
 * `LoginPage` is what actually branches on this, this function only handles
 * converting the raw wire user shape in that case, same as `verifyTwoFactor`
 * below does for the normal path.
 *
 * A credential failure is deliberately generic in both body and status so
 * nothing can be inferred about whether the address exists (FR-03/FR-83) -
 * callers must not elaborate on it.
 */
export async function login(payload: LoginPayload): Promise<LoginOutcome> {
  const { data } = await apiClient.post<LoginChallenge | RawLoginSessionResult>(
    '/auth/login',
    payload,
  );
  if (!data.requires2fa) {
    return { requires2fa: false, accessToken: data.accessToken, user: toSessionUser(data.user) };
  }
  return data;
}

export interface VerifyTwoFactorPayload {
  code: string;
  /** The step-1 credential. Never read from or written to session storage. */
  preAuthToken: string;
}

/**
 * Step 2 of login: exchange the second-factor code plus the step-1 pre-auth
 * credential for the session (FR-04). Possession of the code alone is not
 * enough, and the pre-auth credential is rejected anywhere a session token is
 * expected.
 */
export async function verifyTwoFactor(payload: VerifyTwoFactorPayload): Promise<SessionResponse> {
  const { data } = await apiClient.post<{ accessToken: string; user: RawSessionUser }>(
    '/auth/verify-2fa',
    payload,
  );
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

/** The QR step's material (MYN-241). The secret is for manual entry. */
export interface TotpSetupStart {
  secret: string;
  otpauthUri: string;
}

/**
 * Start authenticator setup for a login that answered `setupRequired`. After an
 * abandoned re-scan the backend answers 403 RECOVERY_CODE_REQUIRED, and the
 * call is repeated with a recovery code (FR-221).
 */
export async function startTotpSetup(preAuthToken: string, recoveryCode?: string): Promise<TotpSetupStart> {
  const { data } = await apiClient.post<TotpSetupStart>('/auth/totp/setup', {
    preAuthToken,
    ...(recoveryCode && { recoveryCode }),
  });
  return data;
}

/** Session plus the one-time recovery codes, shown once after setup. */
export type TotpSetupResult = SessionResponse & { recoveryCodes: string[] };

/** Confirm setup with the first code from the app; signs the user in. */
export async function confirmTotpSetup(payload: VerifyTwoFactorPayload): Promise<TotpSetupResult> {
  const { data } = await apiClient.post<{
    accessToken: string;
    user: RawSessionUser;
    recoveryCodes: string[];
  }>('/auth/totp/setup/verify', payload);
  return {
    accessToken: data.accessToken,
    user: toSessionUser(data.user),
    recoveryCodes: data.recoveryCodes,
  };
}

/** Step 2 of login with a one-time recovery code instead of an app code. */
export async function verifyRecoveryCode(
  payload: VerifyTwoFactorPayload,
): Promise<SessionResponse> {
  const { data } = await apiClient.post<{ accessToken: string; user: RawSessionUser }>(
    '/auth/verify-recovery-code',
    payload,
  );
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

export interface StartTotpRescanPayload {
  currentPassword: string;
  /** A code from the authenticator app in use now. */
  code: string;
}

/** Start an authenticator re-scan for the signed-in user; returns the new QR (FR-220). */
export async function startTotpRescan(payload: StartTotpRescanPayload): Promise<TotpSetupStart> {
  const { data } = await apiClient.post<TotpSetupStart>('/auth/totp/rescan', payload);
  return data;
}

/** Finish a re-scan with the new entry's first code; returns new recovery codes (FR-220). */
export async function verifyTotpRescan(code: string): Promise<string[]> {
  const { data } = await apiClient.post<{ recoveryCodes: string[] }>('/auth/totp/rescan/verify', { code });
  return data.recoveryCodes;
}

/** Replace the signed-in user's recovery codes; the old set stops working. */
export async function regenerateRecoveryCodes(): Promise<string[]> {
  const { data } = await apiClient.post<{ recoveryCodes: string[] }>('/auth/recovery-codes');
  return data.recoveryCodes;
}

export interface ResendTwoFactorPayload {
  preAuthToken: string;
}

/**
 * Re-deliver the second-factor code for an in-flight login (204 on success).
 *
 * Keyed on the pre-auth token rather than an email address, so the endpoint
 * cannot be used to probe whether an address is registered - it is unreachable
 * without having already passed credential verification (FR-03/FR-83).
 *
 * Distinct from `resendOtp` below, which is scoped to signup verification.
 */
export async function resendTwoFactor(payload: ResendTwoFactorPayload): Promise<void> {
  await apiClient.post('/auth/resend-2fa', payload);
}

/** Fetch the current session user (used to hydrate auth state on load). */
export async function getMe(): Promise<SessionUser> {
  const { data } = await apiClient.get<RawSessionUser>('/auth/me');
  return toSessionUser(data);
}

export interface SignupPayload {
  fullName: string;
  email: string;
  password: string;
  tosAccepted: boolean;
  /** Set only by a signup that came from the Issue Report CTA (FR-207); omitted otherwise. */
  intent?: PurchaseIntent;
  /** Required with the Issue Report intent, prefilling its startup step (FR-218). */
  companyName?: string;
  companyWebsite?: string;
}

export interface SignupResult {
  userId: string;
  email: string;
  emailVerified: boolean;
  /** False when the verification email could not be sent (steer user to Resend). */
  otpEmailSent: boolean;
}

export interface VerifyEmailPayload {
  email: string;
  code: string;
}

export interface ResendOtpPayload {
  email: string;
}

/** Register a new individual; the backend emails a verification code (MYN-46). */
export async function signup(payload: SignupPayload): Promise<SignupResult> {
  const { data } = await apiClient.post<SignupResult>('/auth/signup', payload);
  return data;
}

/**
 * Verify the signup email OTP. With 2FA off the backend returns a session.
 * With it on, the authenticator setup challenge while registration's own
 * sign-in is still held (FR-217), else `signInRequired` (MYN-241).
 */
export async function verifyEmail(
  payload: VerifyEmailPayload,
): Promise<SessionResponse | SignInRequired | LoginChallenge> {
  const { data } = await apiClient.post<
    { accessToken: string; user: RawSessionUser } | SignInRequired | LoginChallenge
  >('/auth/verify-email', payload);
  if ('signInRequired' in data || 'requires2fa' in data) return data;
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

/** Request a fresh signup verification code. */
export async function resendOtp(payload: ResendOtpPayload): Promise<void> {
  await apiClient.post('/auth/resend-otp', payload);
}

/**
 * Re-issue the session JWT from current DB state - used after a server-side
 * role change (e.g. becoming a Startup Admin, MYN-46).
 */
export async function refreshSession(): Promise<SessionResponse> {
  const { data } = await apiClient.post<{ accessToken: string; user: RawSessionUser }>(
    '/auth/session/refresh',
  );
  return { accessToken: data.accessToken, user: toSessionUser(data.user) };
}

/**
 * Tell the backend the user is logging out, so the logout can be audited
 * (MYN-146). Returns on the 204; does NOT revoke the token (the session JWT is
 * stateless - see `useLogout`).
 *
 * The bearer token is read synchronously here and attached explicitly, rather
 * than left to the `apiClient` request interceptor: axios runs that interceptor
 * asynchronously (a microtask after this call), and `useLogout` clears the
 * stored token straight after starting this request, so the interceptor alone
 * would send it unauthenticated.
 *
 * Bounded by `LOGOUT_REQUEST_TIMEOUT_MS` so a hung request cannot come back as
 * a 401 after a new login and let the interceptor clear the new token.
 */
export async function logout(): Promise<void> {
  const token = getAccessToken();
  await apiClient.post('/auth/logout', undefined, {
    timeout: LOGOUT_REQUEST_TIMEOUT_MS,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });
}
